import React, { useReducer, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import useSessionStore from '../store/sessionStore'
import { validateFile, processFile } from '../utils/fileUtils'
import DropZone from '../components/upload/DropZone'
import WebcamCapture from '../components/webcam/WebcamCapture'
import VideoRecorder from '../components/webcam/VideoRecorder'
import InlinePreview from '../components/preview/InlinePreview'
import QualityChecklist from '../components/preview/QualityChecklist'
import Button from '../components/ui/Button'
import styles from './Capture.module.css'

const initialState = {
  status: 'UPLOAD_IDLE',
  error: null,
  previewFile: null,
  previewUrl: null,
}

function reducer(state, action) {
  switch (state.status) {
    case 'UPLOAD_IDLE':
      if (action.type === 'file_valid') {
        return {
          status: action.fileType === 'video' ? 'PREVIEW_VIDEO' : 'PREVIEW_IMAGE',
          error: null,
          previewFile: action.file,
          previewUrl: action.previewUrl,
        }
      }
      if (action.type === 'file_invalid') {
        return { ...state, status: 'UPLOAD_ERROR', error: action.error }
      }
      if (action.type === 'start_webcam') {
        return { ...state, status: 'WEBCAM_LOADING', error: null }
      }
      return state

    case 'UPLOAD_ERROR':
      if (action.type === 'retry_upload') {
        return { ...state, status: 'UPLOAD_IDLE', error: null }
      }
      return state

    case 'WEBCAM_LOADING':
      if (action.type === 'webcam_ready') {
        return { ...state, status: 'WEBCAM_ACTIVE', error: null }
      }
      if (action.type === 'webcam_error') {
        return { ...state, status: 'WEBCAM_ERROR', error: action.error }
      }
      return state

    case 'WEBCAM_ACTIVE':
      if (action.type === 'capture_clicked') {
        return {
          status: 'PREVIEW_IMAGE',
          error: null,
          previewFile: action.file,
          previewUrl: action.previewUrl,
        }
      }
      if (action.type === 'start_recording') {
        return { ...state, status: 'RECORDING_ACTIVE' }
      }
      return state

    case 'WEBCAM_ERROR':
      if (action.type === 'retry_webcam') {
        return { ...state, status: 'WEBCAM_LOADING', error: null }
      }
      return state

    case 'RECORDING_ACTIVE':
      if (action.type === 'stop_recording') {
        return { ...state, status: 'RECORDING_STOPPED' }
      }
      return state

    case 'RECORDING_STOPPED':
      if (action.type === 'blob_ready') {
        return {
          status: 'PREVIEW_VIDEO',
          error: null,
          previewFile: action.file,
          previewUrl: action.previewUrl,
        }
      }
      return state

    case 'PREVIEW_IMAGE':
    case 'PREVIEW_VIDEO':
      if (action.type === 'retake_clicked') {
        return { ...state, status: 'RETAKE_CHOICE' }
      }
      if (action.type === 'continue') {
        return state
      }
      return state

    case 'RETAKE_CHOICE':
      if (action.type === 'select_webcam') {
        return {
          ...initialState,
          status: 'WEBCAM_LOADING',
        }
      }
      if (action.type === 'select_upload') {
        return {
          ...initialState,
          status: 'UPLOAD_IDLE',
        }
      }
      return state

    default:
      return state
  }
}

export default function Capture() {
  const navigate = useNavigate()
  const location = useLocation()
  const { selectedType, selectedMethod, addCapturedFile, replaceCapturedFile } = useSessionStore()
  const replaceId = location.state?.replaceId
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    status: selectedMethod === 'webcam' ? 'WEBCAM_LOADING' : 'UPLOAD_IDLE',
  })

  const isVideoType = selectedType === 'video'

  const handleFileSelect = useCallback(
    async (file) => {
      const validation = validateFile(file, isVideoType ? 'video' : 'image')
      if (!validation.valid) {
        dispatch({ type: 'file_invalid', error: validation.error })
        return
      }
      try {
        const processed = await processFile(file, isVideoType ? 'video' : 'image')
        const previewUrl = URL.createObjectURL(processed)
        dispatch({
          type: 'file_valid',
          file: processed,
          previewUrl,
          fileType: isVideoType ? 'video' : 'image',
        })
      } catch (err) {
        dispatch({ type: 'file_invalid', error: err.message || 'Failed to process file.' })
      }
    },
    [isVideoType]
  )

  const handleCapture = useCallback(
    async (file) => {
      const previewUrl = URL.createObjectURL(file)
      dispatch({ type: 'capture_clicked', file, previewUrl })
    },
    []
  )

  const handleWebcamError = useCallback((error) => {
    dispatch({ type: 'webcam_error', error })
  }, [])

  const handleRecorded = useCallback((blob) => {
    const file = new File([blob], 'recording.webm', { type: blob.type || 'video/webm' })
    const previewUrl = URL.createObjectURL(file)
    dispatch({ type: 'blob_ready', file, previewUrl })
  }, [])

  const handleContinue = () => {
    if (state.previewFile) {
      const file = {
        id: crypto.randomUUID(),
        type: selectedType,
        method: selectedMethod,
        originalName: state.previewFile.name,
        mimeType: state.previewFile.type,
        sizeBytes: state.previewFile.size,
        processedBlob: state.previewFile,
        previewUrl: state.previewUrl,
        status: 'ready',
        fileId: null,
        errorMessage: null,
      }
      if (replaceId) {
        replaceCapturedFile(replaceId, file)
        navigate('/finish')
      } else {
        addCapturedFile(file)
        navigate('/preview')
      }
    }
  }

  const isPreview = state.status === 'PREVIEW_IMAGE' || state.status === 'PREVIEW_VIDEO'
  const isRetake = state.status === 'RETAKE_CHOICE'

  return (
    <main className={styles.main}>
      <div className={styles.container}>
        <h1 className={styles.title}>
          {isPreview || isRetake ? 'Review your capture' : 'Capture your document'}
        </h1>
        <p className={styles.sub}>
          {isPreview || isRetake
            ? 'Make sure everything looks correct before continuing.'
            : 'Follow the guide to get the best result.'}
        </p>

        <div className={styles.content}>
          {state.status === 'UPLOAD_IDLE' && (
            <DropZone
              onFileSelect={handleFileSelect}
              onRetry={() => dispatch({ type: 'retry_upload' })}
            />
          )}

          {state.status === 'UPLOAD_ERROR' && (
            <DropZone
              onFileSelect={handleFileSelect}
              error={state.error}
              onRetry={() => dispatch({ type: 'retry_upload' })}
            />
          )}

          {(state.status === 'WEBCAM_LOADING' || state.status === 'WEBCAM_ACTIVE') &&
            !isVideoType && (
              <WebcamCapture
                type={selectedType}
                onCapture={handleCapture}
                onError={handleWebcamError}
              />
            )}

          {state.status === 'WEBCAM_ERROR' && (
            <div className={styles.errorBox} role="alert">
              <p>{state.error}</p>
              <Button onClick={() => dispatch({ type: 'retry_webcam' })}>Try again</Button>
            </div>
          )}

          {(state.status === 'WEBCAM_LOADING' || state.status === 'WEBCAM_ACTIVE' || state.status === 'RECORDING_ACTIVE' || state.status === 'RECORDING_STOPPED') &&
            isVideoType && (
              <VideoRecorder onRecorded={handleRecorded} />
            )}

          {isPreview && (
            <div className={styles.previewPhase}>
              <InlinePreview
                file={state.previewFile}
                previewUrl={state.previewUrl}
                type={selectedType}
              />
              <div className={styles.checklistWrap}>
                <QualityChecklist type={selectedType} />
              </div>
              <div className={styles.actions}>
                <Button
                  variant="ghost"
                  onClick={() => dispatch({ type: 'retake_clicked' })}
                >
                  Retake or replace
                </Button>
                <Button onClick={handleContinue}>Looks good, continue</Button>
              </div>
            </div>
          )}

          {isRetake && (
            <div className={styles.retakeChoice}>
              <p className={styles.retakeTitle}>How would you like to recapture?</p>
              <div className={styles.retakeButtons}>
                <Button onClick={() => dispatch({ type: 'select_webcam' })}>
                  Take again with webcam
                </Button>
                <Button variant="secondary" onClick={() => dispatch({ type: 'select_upload' })}>
                  Upload a different file
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </main>
  )
}
